/**
 * SHARED GLOBAL ERROR REPORTING — call installGlobalErrorReporting() once in
 * main.tsx / index.tsx BEFORE rendering. Canonical copy: /shared/frontend/.
 * Synced into portals by scripts/sync-shared-frontend.sh.
 *
 * Provides:
 *  1. window.fetch patch      -> every 4xx/5xx API response logs
 *                                console.error(response) (works even in portals
 *                                that do not use axios).
 *  2. attachAxiosInterceptor  -> same behaviour for axios-based portals;
 *                                strictly executes console.error(error.response).
 *  3. window error handlers   -> uncaught errors / unhandled promise rejections
 *                                logged with full stack.
 *  4. showToast(message)      -> dependency-free DOM toast, kept exported for
 *                                callers that want to raise an explicit UI
 *                                message. HTTP status errors (400/403/500…) are
 *                                NOT toasted: they are logged to the console
 *                                only, so a failing request never paints a red
 *                                banner across the portal UI.
 */

let toastRoot: HTMLDivElement | null = null;

export function showToast(message: string, type: 'error' | 'info' = 'error') {
  try {
    if (!toastRoot) {
      toastRoot = document.createElement('div');
      toastRoot.style.cssText = 'position:fixed;top:16px;right:16px;z-index:99999;display:flex;flex-direction:column;gap:8px;';
      document.body.appendChild(toastRoot);
    }
    const el = document.createElement('div');
    el.style.cssText = `max-width:360px;padding:10px 14px;border-radius:8px;font:13px system-ui,sans-serif;color:#fff;box-shadow:0 4px 12px rgba(0,0,0,.25);background:${type === 'error' ? '#dc2626' : '#2563eb'};`;
    el.textContent = message;
    toastRoot.appendChild(el);
    setTimeout(() => el.remove(), 6000);
  } catch { /* never let the reporter crash the app */ }
}

function reportHttpError(method: string, url: string, status: number, body: unknown) {
  const msg = `API error ${status} — ${method.toUpperCase()} ${url}`;
  console.error(msg, body);
}

/** Axios interceptor (pass the portal's axios instance or the default import). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function attachAxiosInterceptor(axios: any) {
  axios.interceptors.response.use(
    (res: unknown) => res,
    (error: any) => {
      if (error?.response) {
        // Callers can opt a request out of global reporting (e.g. an expected
        // 403 that the UI already handles with its own message).
        if (!error.config?.suppressGlobalError) {
          reportHttpError(error.config?.method || 'GET', error.config?.url || '?', error.response.status, error.response.data);
          console.error(error.response); // strict requirement
        }
      } else {
        console.error('[api] network error:', error);
      }
      return Promise.reject(error);
    }
  );
}

export function installGlobalErrorReporting() {
  // 1. fetch patch (idempotent)
  const w = window as any;
  if (!w.__fetchErrorReporterInstalled) {
    w.__fetchErrorReporterInstalled = true;
    const origFetch = window.fetch.bind(window);
    window.fetch = async (...args: Parameters<typeof fetch>) => {
      const url = typeof args[0] === 'string' ? args[0] : (args[0] as Request).url;
      const method = (args[1]?.method || (typeof args[0] !== 'string' && (args[0] as Request).method) || 'GET') as string;
      try {
        const res = await origFetch(...args);
        // Only report API-ish failures; 401 redirects are normal auth flow noise.
        if (!res.ok && res.status !== 401 && (url.includes('/api/') || url.startsWith('/'))) {
          reportHttpError(method, url, res.status, await res.clone().text().catch(() => ''));
        }
        return res;
      } catch (err) {
        console.error('[fetch] network failure:', method, url, err);
        throw err;
      }
    };
  }
  // 2. window-level crash logging
  window.addEventListener('error', (e) =>
    console.error('[window] uncaught error:', e.error || e.message, e.error?.stack || ''));
  window.addEventListener('unhandledrejection', (e) =>
    console.error('[window] unhandled promise rejection:', e.reason, e.reason?.stack || ''));
}
